import React from 'react';
import { CheckBoxOutlineBlankOutlined, CheckBoxOutlined, FileDownloadOutlined } from "@mui/icons-material";
import { Checkbox, FormControlLabel, IconButton, Tooltip } from "@mui/material";
import { toast } from "react-toastify";
import moment from "moment";

import AppConfirmDialog from "components/Shared/AppConfirmDialog";
import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import DateRangeToggle from "components/Shared/DateRangeToggle";

import { Controller, useForm } from "react-hook-form";

import { useLazyGetDDLVehicleListQuery } from "store/services/vehicleApi";
import { useLazyGetVehicleMileageReportQuery } from "store/services/reportApi";

import { exportExcelFile } from "utils/exportExcelFile";
import { useAuth } from "hooks";

const EXCEL_DATA_INITIAL_STATE = {
    dateRange: [],
    vehicleId: null,
};

const ExportVehicleMileageExcel = ({ isBusy = false }) => {
    const [showDialog, setShowDialog] = React.useState(false);
    const [showForm, setShowForm] = React.useState(false);

    const { viewEntity } = useAuth();

    const {
        control,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm({
        defaultValues: EXCEL_DATA_INITIAL_STATE,
    });

    // RTK Query implementation
    const [fetchVehicleMileage] = useLazyGetVehicleMileageReportQuery();
    const [fetchVehicles, { data: vehicles, isLoading: loadingVehicles }] =
        useLazyGetDDLVehicleListQuery();

    React.useEffect(() => {
        fetchVehicles({
            account: viewEntity?.entity?.account?._id,
            entity: viewEntity?.entity?._id,
        });
    }, [
        fetchVehicles,
        viewEntity?.entity?._id,
        viewEntity?.entity?.account?._id,
    ]);

    const handleDownload = async (data) => {
        const { dateRange } = data;

        if (!dateRange || dateRange.length !== 2) {
            toast.error("Please select a valid date range.");
            return;
        }

        const startDate = moment(dateRange[0]).format("YYYY-MM-DD");
        const endDate = moment(dateRange[1]).format("YYYY-MM-DD");

        const queryParams = {
            params: {
                vehicleId: data?.vehicleId?._id,
                startDate,
                endDate,
            },
        };

        try {
            const responseData = await fetchVehicleMileage(queryParams).unwrap();

            if (!responseData?.data || responseData.data.length === 0) {
                toast.info("No mileage records found for the selected date range and vehicle.");
                return;
            }

            const formattedData = responseData.data.map((row, index) => ({
                SN: index + 1,
                "Vehicle ID": row.vehicle?.vehicle_id || "N/A",
                "Registration Number": row.vehicle?.registration_number || "N/A",
                "Recent Mileage (km/L)": row.summary?.recentMileage ?? "N/A",
                "Average Mileage (km/L)": row.summary?.averageMileage ?? "N/A",
                "Total Fuel Quantity (L)": row.summary?.totalFuelQuantityAll ?? "N/A",
                "Total Fuel Cost (Rs.)": row.summary?.totalFuelCost?.toFixed(2) ?? "N/A",
            }));

            // Add summary row
            const summary = responseData?.summary;
            if (summary) {
                formattedData.push({
                    SN: "",
                    "Vehicle ID": "SUMMARY",
                    "Registration Number": "",
                    "Recent Mileage (km/L)": "",
                    "Average Mileage (km/L)": summary.averageMileage || 0,
                    "Total Fuel Quantity (L)": summary.totalFuelQuantityAll || 0,
                    "Total Fuel Cost (Rs.)": summary.totalFuelCost?.toFixed(2) || 0,
                });
            }

            const fileName = `VehicleMileage_${startDate}_to_${endDate}`;
            exportExcelFile({ sheetData: formattedData, fileName });
            toast.success("Mileage data downloaded successfully!");
        } catch (error) {
            console.error("Download error:", error);
            toast.error("Failed to download mileage data");
        }
        handleReset();
    };

    // methods
    const handleReset = () => {
        setShowDialog(false);
        reset(EXCEL_DATA_INITIAL_STATE);
    };

    return (
        <>
            <Tooltip title="Download Excel" arrow>
                <IconButton color="success" onClick={() => setShowDialog(true)}>
                    <FileDownloadOutlined />
                </IconButton>
            </Tooltip>

            <AppConfirmDialog
                open={showDialog}
                onClose={handleReset}
                onConfirm={handleSubmit(handleDownload)}
                title="Download Vehicle Mileage Excel"
                dialogIcon={() => <FileDownloadOutlined color="success" />}
            >
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={showForm}
                            onChange={(e) => setShowForm(e.target.checked)}
                            icon={<CheckBoxOutlineBlankOutlined />}
                            checkedIcon={<CheckBoxOutlined />}
                        />
                    }
                    label="Filter by specific vehicle"
                />
                {showForm && (
                    <AppForm loading={isBusy}>
                        <AppForm.Content>
                            <Controller
                                name="vehicleId"
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                    <BaseAutocomplete
                                        fullWidth
                                        label="Vehicle ID (Optional)"
                                        value={value || null}
                                        onChange={(_, data) => onChange(data)}
                                        getOptionLabel={(option) => option?.vehicle_id || ""}
                                        isOptionEqualToValue={(option, value) =>
                                            option?._id === value?._id
                                        }
                                        options={vehicles || []}
                                        loading={loadingVehicles}
                                        loadingText="Loading Vehicles..."
                                        helperText="Leave empty to include all vehicles"
                                    />
                                )}
                            />
                        </AppForm.Content>
                    </AppForm>
                )}
                <Controller
                    name="dateRange"
                    control={control}
                    rules={{ required: "Please select date range." }}
                    render={({ field, fieldState: { error } }) => (
                        <DateRangeToggle
                            {...field}
                            label="Select Date Range"
                            error={!!error}
                            helperText={error?.message}
                        />
                    )}
                />
            </AppConfirmDialog>
        </>
    );
};

export default ExportVehicleMileageExcel;